/**
 * /pricing — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * ── Why this reads from the API and not from a constant ───────────────────
 *
 * The previous version of this page held the prices in an array at the top of
 * the file, under a comment insisting they were "the real ones". They were,
 * until they were not: changing one meant a deploy, so the page and the
 * agreement drifted, and the page was eventually deleted rather than kept
 * true. Plans are rows now. This renders whatever /api/plans/public says is
 * listed and sellable, which is the same source the admin edits and the same
 * source a customer subscribes against — three surfaces that can no longer
 * disagree with each other.
 *
 * ── The figures in the comparison block ───────────────────────────────────
 *
 * COMPONENT_COSTS are competitors' published list prices, checked rather than
 * remembered, and deliberately conservative — the argument holds without
 * inflating the alternative. They are labelled as market rates in the UI, not
 * as quotes, because that is what they are.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@bloomneo/uikit';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { SEO } from '@/shared/SEO';
import { CurrencyToggle, money, priceFor, useCurrency } from '@/shared/currency';
import { api } from '@/shared/api';
import type { Envelope } from '@/features/dns/components/dns-shared';

interface Plan {
  id: number;
  key: string;
  name: string;
  tagline: string | null;
  priceMinor: string;
  /** Set when the package is sold in rupees; null when it is not. */
  inrPriceMinor?: string | null;
  currency: string;
  interval: string;
  highlighted: boolean;
  maxApps: number;
  maxDomains: number;
  storageGb: number;
  emailsPerMonth: number;
  backupRetentionDays: number;
  supportMinutesPerMonth: number;
  responseHours: number | null;
  namedContact: boolean;
  stagingIncluded: boolean;
  overageRateMinor: string | null;
}

/**
 * Render's published rates for the same resources, September 2026: compute,
 * managed Postgres, the workspace seat a production team needs, and disk.
 * The point of the row is not Render specifically — it is that the parts cost
 * more than the whole even before anybody answers the phone.
 */
const COMPONENT_COSTS = [
  { label: 'Application compute', note: null, basic: 85, standard: 175, enterprise: 300 },
  { label: 'Managed Postgres', note: null, basic: 19, standard: 40, enterprise: 55 },
  { label: 'Team workspace', note: null, basic: 25, standard: 25, enterprise: 25 },
  { label: 'Disk', note: null, basic: 6, standard: 25, enterprise: 125 },
  /*
   * The two lines a hosting bill never shows, because no host sells them.
   * Both are things bloomneo does on every plan and both cost real money to
   * arrange yourself — one-minute checks with someone paged, and dumps held
   * somewhere other than the machine that made them.
   */
  { label: 'Uptime monitoring and alerting', note: 'one-minute checks, paged', basic: 25, standard: 35, enterprise: 50 },
  { label: 'Offsite backups', note: 'held away from the server', basic: 8, standard: 15, enterprise: 35 },
];

/**
 * The row the hosting comparison leaves out, and the one that decides the
 * argument.
 *
 * Infrastructure alone is a fair comparison at the smaller plans and a
 * misleading one at the largest, where what is being bought is mostly
 * somebody's attention. Valued at a deliberately low contractor rate — the
 * point survives without inflating it, and a reader who pays more than this
 * for an engineer will adjust in our favour on their own.
 */
const LABOUR_RATE_USD = 30;

/**
 * The dollar figures in the comparison table below, which are quoted in USD
 * whatever the viewer is being billed in — they are what the named vendors
 * actually charge, and a rupee column would be a conversion we invented.
 */
function usd(minor: string): string {
  const n = Number(minor) / 100;
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function thousands(n: number): string {
  return n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
}

/**
 * What the plan entitles you to, in the order people ask about it.
 *
 * ── Capabilities, not hours ───────────────────────────────────────────────
 *
 * This listed "4h 20m of our time each month" and an hourly overage rate,
 * which described the product as labour sold by the hour. Creem's review
 * puts "services of any kind… consulting and similar work" on its RESTRICTED
 * list — not banned, but requiring prior processor history and a proven track
 * record, which a first application cannot have. A page that reads as a
 * services business routes itself into that queue.
 *
 * It is also the less true description. What a customer buys is a platform
 * that renews certificates, takes backups nightly and watches the sites; the
 * hours are how bloomneo resources that, not what is being sold. They remain
 * on the plan record and in the admin, because they are a real internal
 * limit — they are simply not the product.
 *
 * Response times stay. Every software company publishes them, and a
 * commitment to answer is not the same as selling the hour.
 */
function included(p: Plan): string[] {
  return [
    `${p.maxApps} application${p.maxApps === 1 ? '' : 's'}`,
    `${p.maxDomains} domains, SSL issued and auto-renewed`,
    `Automated backups, kept ${p.backupRetentionDays} days`,
    'Uptime monitoring with alerts',
    `${p.storageGb} GB storage · ${thousands(p.emailsPerMonth)} emails a month`,
    p.stagingIncluded ? 'Staging environment' : null,
    p.responseHours ? `${p.responseHours}-hour support response` : 'Email support, business hours',
    p.namedContact ? 'A named contact who knows your setup' : null,
  ].filter(Boolean) as string[];
}

export default function PricingPage() {
  const navigate = useNavigate();
  /* Defaults from the browser's timezone, remembers a correction, and decides
     nothing about what anybody is charged — the gateway and the amount are
     settled server-side when a checkout is created. */
  const { currency, setCurrency } = useCurrency();
  const [plans, setPlans] = React.useState<Plan[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    api.get<Envelope<Plan[]>>('/api/plans/public')
      .then((r) => setPlans(r.data))
      .catch(() => setError('Could not load the plans just now. /contact reaches us either way.'));
  }, []);

  const infra = COMPONENT_COSTS.reduce(
    (acc, row) => ({
      basic: acc.basic + row.basic,
      standard: acc.standard + row.standard,
      enterprise: acc.enterprise + row.enterprise,
    }),
    { basic: 0, standard: 0, enterprise: 0 },
  );

  /*
   * Keyed by plan key rather than by position, so reordering the plans in the
   * admin cannot silently pair Basic's price with Enterprise's column.
   */
  const COLUMN = ['basic', 'standard', 'enterprise'] as const;
  const labourFor = (p: Plan) => Math.round((p.supportMinutesPerMonth / 60) * LABOUR_RATE_USD);
  const separatelyFor = (p: Plan) => {
    const col = COLUMN.find((c) => c === p.key);
    return (col ? infra[col] : 0) + labourFor(p);
  };
  /** Null when we are not actually cheaper — an honest page can say that. */
  const savingFor = (p: Plan): number | null => {
    const alt = separatelyFor(p);
    const ours = Number(p.priceMinor) / 100;
    if (!alt || ours >= alt) return null;
    return Math.round(((alt - ours) / alt) * 100);
  };

  return (
    <>
      <SEO
        title="Pricing | Bloomneo"
        description="Three managed plans: servers, domains, certificates, databases, backups, email and monitoring, operated for you. From $50 a month."
      />

      <div className="space-y-16 sm:space-y-20">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <section className="bloom-rise max-w-2xl">
          <h1
            className="font-bold tracking-[-0.02em] leading-[1.05]"
            style={{ fontSize: 'clamp(1.9rem, 4.4vw, 3rem)' }}
          >
            One bill for the whole layer.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every plan includes the servers, the domains, the certificates, the database and its
            backups, transactional mail and the monitoring around all of it — plus the people who
            keep them running. The difference between plans is how much you run and how quickly we
            answer.
          </p>
        </section>

        {/* ── The plans ───────────────────────────────────────────────── */}
        <section>
          {/*
            Beside the prices, not buried in a footer. Somebody shown the
            wrong currency has to be able to see the way to fix it without
            hunting for it, and somebody shown the right one barely notices.
          */}
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {currency === 'INR'
                ? 'Billed in rupees through Razorpay.'
                : 'Billed in US dollars through Creem, our merchant of record.'}
            </p>
            <CurrencyToggle currency={currency} onChange={setCurrency} />
          </div>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : !plans ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading plans…
            </p>
          ) : (
            <div className="grid gap-5 lg:grid-cols-3">
              {plans.map((p) => (
                <div
                  key={p.id}
                  className={`bloom-rise flex flex-col rounded-2xl border p-6 sm:p-7 ${
                    p.highlighted ? 'border-primary bg-primary/[0.03]' : 'border-border'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h2 className="text-lg font-semibold">{p.name}</h2>
                    {/*
                      "Most chosen" was a claim about other customers, and it
                      was not one we could evidence — there is no purchase
                      history behind it. An unverifiable popularity statistic
                      is exactly what a payment review reads as manufactured
                      social proof, and it would have been the only sentence on
                      the page that was not checkable.

                      "Recommended" says the same thing about which package to
                      pick while only claiming our own opinion, which is always
                      true. The plan's tagline underneath names who it is for.
                    */}
                    {p.highlighted && (
                      <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
                        Recommended
                      </span>
                    )}
                  </div>
                  {p.tagline && (
                    <p className="mt-1.5 text-sm text-muted-foreground">{p.tagline}</p>
                  )}

                  <p className="mt-5 flex items-baseline gap-1.5">
                    <span className="text-3xl font-bold tracking-tight">
                      {priceFor(p, currency) ? money(priceFor(p, currency), currency) : '—'}
                    </span>
                    <span className="text-sm text-muted-foreground">/{p.interval}</span>
                  </p>
                  {/* A package with no rupee price is one India cannot buy.
                      Said plainly rather than shown as a converted guess. */}
                  {currency === 'INR' && !p.inrPriceMinor && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Not sold in rupees — <button type="button" className="underline underline-offset-2" onClick={() => setCurrency('USD')}>see the USD price</button>
                    </p>
                  )}
                  {/* Only where it is true. A plan we have not actually beaten
                      says nothing rather than rounding its way to a claim. */}
                  {savingFor(p) !== null && (
                    <p className="mt-2 text-xs">
                      <span className="rounded-full bg-success/10 px-2 py-0.5 font-medium text-success">
                        {savingFor(p)}% less
                      </span>
                      <span className="ml-2 text-muted-foreground">
                        than ${separatelyFor(p)} assembled yourself
                      </span>
                    </p>
                  )}

                  <ul className="mt-6 space-y-2.5">
                    {included(p).map((line) => (
                      <li key={line} className="flex items-start gap-2.5 text-sm">
                        <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                        <span className="text-muted-foreground">{line}</span>
                      </li>
                    ))}
                  </ul>

                  {/* The hourly overage line lived here. Removed with the
                      hours themselves — see included() above. */}

                  {/*
                    A way to buy, not a way to enquire.
                    
                    Every button here said "Talk to us", which made the page a
                    brochure: a reader could learn the price and still had no
                    route to becoming a customer except writing an email. It
                    also reads badly to a payment provider reviewing whether
                    this is a product somebody can actually purchase.
                    
                    /auth/register is the real self-serve path — an account,
                    then a package chosen from Billing. The plan key rides
                    along in the query so the choice made here is not made
                    again two screens later.
                    
                    The route is /AUTH/register, which this got wrong once by
                    reading it off the filename: pages/register.tsx is served
                    at /auth/register, and nginx answers any unknown path with
                    the app shell, so /register returned 200 and rendered
                    "Page not found" inside it. A link that 404s politely is
                    worse than no link, and a 200 will not tell you.
                    
                    "Talk to us" stays below for the people who want a
                    conversation first, which on a managed-infrastructure
                    purchase is most of them.
                  */}
                  <Button
                    className="mt-6 w-full rounded-full gap-2"
                    variant={p.highlighted ? 'default' : 'outline'}
                    onClick={() => navigate(`/auth/register?plan=${encodeURIComponent(p.key)}`)}
                  >
                    Get started <ArrowRight className="h-4 w-4" />
                  </Button>
                  <button
                    type="button"
                    onClick={() => navigate('/contact')}
                    className="mt-2 w-full cursor-pointer text-center text-xs text-muted-foreground hover:text-foreground"
                  >
                    or talk to us first
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── What the parts cost ─────────────────────────────────────── */}
        <section className="bloom-rise rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 md:p-10">
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.05] mb-3"
            style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
          >
            The same resources, bought separately
          </h2>
          <p className="text-muted-foreground max-w-xl mb-8">
            Market rates for equivalent managed compute, database, workspace and disk — before
            anyone monitors it, renews a certificate, or answers a message. These are published
            list prices, not quotes.
            {/*
              This whole table is in dollars whichever currency is selected
              above, because every figure in it is what a named vendor
              actually charges. A rupee column would be a conversion we
              invented, which is the one thing a page of real list prices
              must not quietly do.
            */}
            {currency === 'INR' && (
              <span className="mt-2 block text-sm">
                Figures below are in US dollars — they are the vendors' own published prices.
              </span>
            )}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Piece</th>
                  {plans?.map((p) => (
                    <th key={p.id} className="pb-2 text-right font-medium text-muted-foreground">{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPONENT_COSTS.map((row) => (
                  <tr key={row.label} className="border-b border-border/60">
                    <td className="py-2 text-muted-foreground">
                      {row.label}
                      {row.note && (
                        <span className="block text-[11px] text-muted-foreground/70">{row.note}</span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">${row.basic}</td>
                    <td className="py-2 text-right tabular-nums">${row.standard}</td>
                    <td className="py-2 text-right tabular-nums">${row.enterprise}</td>
                  </tr>
                ))}
                <tr className="border-b border-border/60">
                  <td className="py-2 text-muted-foreground">
                    {/* Costed from the plan's internal support budget, but
                        described as what it would cost the READER to hire —
                        which is the comparison being made. The figure is the
                        same; the sentence is about their alternative, not
                        about an hourly rate we charge. */}
                    Somebody to operate it
                    <span className="block text-[11px] text-muted-foreground/70">
                      at a ${LABOUR_RATE_USD}/hour contractor rate
                    </span>
                  </td>
                  {plans?.map((p) => (
                    <td key={p.id} className="py-2 text-right tabular-nums">${labourFor(p)}</td>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2.5 font-medium">Separately</td>
                  {plans?.map((p) => (
                    <td key={p.id} className="py-2.5 text-right font-medium tabular-nums">${separatelyFor(p)}</td>
                  ))}
                </tr>
                <tr>
                  <td className="py-2.5 font-medium text-primary">With us, operated</td>
                  {plans?.map((p) => (
                    <td key={p.id} className="py-2.5 text-right font-semibold tabular-nums text-primary">
                      {usd(p.priceMinor)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="pt-1 text-xs text-muted-foreground">You save</td>
                  {plans?.map((p) => {
                    const pct = savingFor(p);
                    return (
                      <td key={p.id} className="pt-1 text-right text-xs font-medium tabular-nums text-success">
                        {pct === null ? '—' : `${pct}%`}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-sm text-muted-foreground max-w-xl">
            That row is costed at a contractor rate well under what most people pay, and it still
            only buys somebody's time — not the monitoring that notices at 3am, not the renewal
            nobody had to remember, and not a person who already knows how your release works.
            Those are the part you cannot put in a cart.
          </p>
        </section>

        {/* ── The honest small print ──────────────────────────────────── */}
        <section className="bloom-rise max-w-2xl space-y-5">
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.05]"
            style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
          >
            What these numbers do not cover
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              <strong className="text-foreground">Application development.</strong> Support covers
              the platform — DNS, certificates, restores, environment changes, failed deploys, mail
              deliverability, performance investigation. Changes to your own code are quoted
              separately, because an open-ended promise to fix anything is one nobody keeps.
            </p>
            <p>
              <strong className="text-foreground">Out of hours.</strong> We are here Monday to
              Friday, 10am to 7pm IST. A critical incident — production down, or data at risk — is
              worked immediately during those hours on every plan, including Basic. Enterprise adds
              a named contact reachable outside them.
            </p>
            <p>
              <strong className="text-foreground">Going over.</strong> Storage and email are
              reported, never cut off. We will tell you before an invoice does. Applications and
              domains are capped, because those we would have to build for you anyway.
            </p>
            <p>
              The full commitments — uptime targets, response times, and what you are owed when we
              miss them — are on the{' '}
              <button type="button" onClick={() => navigate('/sla')} className="cursor-pointer text-primary hover:underline">
                service level agreement
              </button>.
            </p>
          </div>
          <Button className="rounded-full gap-2" size="lg" onClick={() => navigate('/contact')}>
            Talk to us <ArrowRight className="h-4 w-4" />
          </Button>
        </section>
      </div>
    </>
  );
}
